import { Button, Col, Modal, Row, Tooltip } from 'antd';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { WarningOutlined } from '@ant-design/icons';

import * as St from './SmallProjectModal.style';

import { useLazyValidateTicket } from '~/api/easy-estimator-api/tickets-api/useLazyValidateTicket';
import { SMALL_JOB_AREA } from '~/core/constants';
import { useAppSelector } from '~/redux/hooks';
import { selectValidSmallTicketNumber } from '~/redux/slices/estimate';

interface SmallProjectModalProps {
  value?: string;
  isVisible?: boolean;
  minSqFt?: number;
  onCancel?: () => void;
  onContinue: () => void;
  onSuccess: (ticketNumber: string) => void;
  onChange?: (ticketNumber: string) => void;
}

/**
 *
 * @param onCancel ()=>void action to take on cancel button click
 * @param onContinue ()=>void action to take on "Continue with no code" button
 * @param onSuccess (ticketNumber)=>void action to take after verifying the ticket number
 * @param onChange (value)=>void action taken on changing the value
 * @param minSqFt minimum square feet used for this material
 * @param isVisible
 * @param value initial value of the ticket number - should only ever persist if it was valid
 */

const CODE_MIN_LENGTH = 8;
const CODE_MAX_LENGTH = 8;

export const SmallProjectModal = ({
  onCancel,
  onContinue,
  onSuccess,
  onChange,
  minSqFt,
  isVisible,
}: SmallProjectModalProps): JSX.Element => {
  const { t } = useTranslation();
  const validatedTicketNumber = useAppSelector(selectValidSmallTicketNumber);

  const { validateTicket } = useLazyValidateTicket();
  const [tempTicketNumber, setTempTicketNumber] = useState<string | undefined>(
    undefined
  );
  const [shouldShowError, setShouldShowError] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  const ticketNumber = useMemo(
    () => tempTicketNumber ?? validatedTicketNumber,
    [tempTicketNumber, validatedTicketNumber]
  );

  const onTicketNumberChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setShouldShowError(false);
      const cleanTicketNumber = e.target.value
        .replace(/[\W_]/g, '')
        .toUpperCase();

      setTempTicketNumber(cleanTicketNumber);
      if (onChange) {
        onChange(cleanTicketNumber);
      }
    },
    [onChange]
  );

  const onSmallProjectVerify = async (ticketNumber: string) => {
    setIsValidating(true);
    const result = await validateTicket({ ticketNumber });

    setIsValidating(false);
    const isValidTicket =
      result.isSuccess && result.data?.ticketNumber === ticketNumber;

    setShouldShowError(!isValidTicket);
    if (isValidTicket) {
      onSuccess(ticketNumber); // success, return the valid result
    }
  };

  const isTicketVerifiable = useCallback(
    (ticketNumber: string): boolean =>
      ticketNumber.length >= CODE_MIN_LENGTH &&
      ticketNumber.length <= CODE_MAX_LENGTH,
    []
  );

  return (
    <Modal
      centered
      destroyOnClose
      open={isVisible}
      closable={false}
      maskClosable
      footer={null}
      wrapProps={{ span: 24 }}
    >
      <St.Title>{t('SMALL_PROJECTS.TITLE')}</St.Title>
      <St.Notification>
        {t('SMALL_PROJECTS.NOTIFICATION', {
          minSqFt: minSqFt ?? SMALL_JOB_AREA.DEFAULT_HIGH,
        })}
      </St.Notification>
      <div>
        <St.TicketInput
          key="ticketNumber"
          maxLength={CODE_MAX_LENGTH}
          minLength={CODE_MIN_LENGTH}
          allowClear
          value={ticketNumber}
          disabled={isValidating}
          placeholder="Permission Code"
          status={shouldShowError ? 'error' : undefined}
          addonBefore={
            shouldShowError ? (
              <Tooltip title={t('SMALL_PROJECTS.ERROR.INVALID')}>
                <WarningOutlined />
              </Tooltip>
            ) : null
          }
          addonAfter={
            <Button
              key="validateButton"
              disabled={isValidating || !isTicketVerifiable(ticketNumber ?? '')}
              loading={isValidating}
              onClick={() => {
                onSmallProjectVerify(ticketNumber ?? '');
              }}
            >
              {t('SMALL_PROJECTS.BUTTONS.VERIFY')}
            </Button>
          }
          size="large"
          onChange={onTicketNumberChange}
        />
        {shouldShowError && (
          <St.ErrorText>{t('SMALL_PROJECTS.ERROR.INVALID')}</St.ErrorText>
        )}
        <Row justify="end" gutter={8}>
          <Col>
            <St.FooterButton key="continueButton" onClick={onContinue}>
              {t('SMALL_PROJECTS.BUTTONS.CONTINUE')}
            </St.FooterButton>
          </Col>
          <Col>
            <St.FooterButton key="cancelButton" onClick={onCancel}>
              {t('SMALL_PROJECTS.BUTTONS.CANCEL')}
            </St.FooterButton>
          </Col>
        </Row>
      </div>
    </Modal>
  );
};
